import { DateTime } from "luxon";

export function formatRelativeDayLabel(iso: string): string {
  if (!iso) return "";
  let dt: DateTime;
  try {
    dt = DateTime.fromISO(iso);
  } catch {
    return "";
  }

  const day = dt.startOf("day");
  const today = DateTime.now().startOf("day");
  const yesterday = today.minus({ days: 1 });
  const tomorrow = today.plus({ days: 1 });

  if (day.equals(today)) return "Today";
  if (day.equals(yesterday)) return "Yesterday";
  if (day.equals(tomorrow)) return "Tomorrow";

  return dt.toFormat("cccc, LLL d, yyyy");
}

export function formatMentionedAgo(addedAt: string | null, _tick?: unknown): string {
  // `_tick` is intentionally unused; consumers pass a periodically changing value
  // to force recomputation for "mentioned X min ago" labels.
  if (!addedAt) return "mentioned just now";
  const added = DateTime.fromISO(addedAt);
  if (!added.isValid) return "mentioned just now";
  const diffMinutes = Math.floor(DateTime.now().diff(added, "minutes").minutes);
  if (diffMinutes < 1) return "mentioned just now";
  if (diffMinutes === 1) return "mentioned 1 min ago";
  if (diffMinutes >= 60) return "mentioned over an hour ago";
  return `mentioned ${diffMinutes} min ago`;
}

function formatRecordingOffset(recordingOffsetSeconds: number): string {
  const totalSeconds = Math.floor(recordingOffsetSeconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours}${hours === 1 ? "hr" : "hrs"}`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}min`);
  }
  if (seconds > 0 || parts.length === 0) {
    parts.push(`${seconds}s`);
  }

  return `${parts.join("")} in`;
}

export function formatLiveItemTimestamp({
  addedAt,
  recordingOffsetSeconds,
  tick,
}: {
  addedAt: string | null;
  recordingOffsetSeconds: number | null | undefined;
  tick?: unknown;
}): string {
  if (
    typeof recordingOffsetSeconds === "number" &&
    Number.isFinite(recordingOffsetSeconds) &&
    recordingOffsetSeconds >= 0
  ) {
    return formatRecordingOffset(recordingOffsetSeconds);
  }

  return formatMentionedAgo(addedAt, tick);
}
